import { useAttachmentPolicy } from "../../components/Foro/useAttachmentPolicy";
import { AttachmentPicker } from "../../components/Foro/AttachmentPicker";
import "../../components/Foro/attachments.css";
import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getForumDiscussions,
  getForumCategories,
  createForumDiscussion,
  togglePinDiscussion,
  deleteForumDiscussion,
} from "../../services/moodle/forum";
import { DiscussionList } from "../../components/Foro/DiscussionList";
import { DiscussionCardSkeleton } from "../../components/Foro/ForumSkeleton";
import DeleteConfirmationModal from "../../components/Foro/DeleteConfirmationModal";
import { EmojiPickerButton } from "../../components/Foro/EmojiPickerButton";
import { Plus, X, MessageSquare, ArrowLeft, ChevronRight } from "lucide-react";
import { getCategoryColor } from "../../components/Foro/utils";

const CategoryView = () => {
  const { categoryId } = useParams();
  const theme = useTheme();
  const auth = useAuth();
  const { policy: attachmentPolicy, error: policyError } = useAttachmentPolicy();
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mutationError, setMutationError] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [discussionToDelete, setDiscussionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const lastFocusedFieldRef = useRef("content");
  const isDark = theme.mode === "dark";

  const insertIntoField = (ref, currentVal, setter, emoji) => {
    const el = ref.current;
    if (!el) {
      setter((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? currentVal.length;
    const end = el.selectionEnd ?? currentVal.length;
    const updated = currentVal.substring(0, start) + emoji + currentVal.substring(end);
    setter(updated);

    requestAnimationFrame(() => {
      el.focus();
      const newPos = start + emoji.length;
      el.setSelectionRange(newPos, newPos);
    });
  };

  const handleEmojiSelect = (emoji, targetField) => {
    const field = targetField || lastFocusedFieldRef.current;
    if (field === "title") {
      insertIntoField(titleRef, newTitle, setNewTitle, emoji);
    } else {
      insertIntoField(contentRef, newContent, setNewContent, emoji);
    }
  };

  const { data: categoriesData = [] } = useQuery({
    queryKey: ["forum", "categories"],
    queryFn: async () => {
      const data = await getForumCategories({ token: auth.user.token });
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(auth.user?.token),
    staleTime: 10 * 60_000,
  });

  const category = categoriesData?.find((c) => c.id === Number(categoryId));
  const categoryName = category?.name || "";

  const {
    data: discussions = [],
    isLoading: loading,
    error: discussionsError,
    refetch: fetchDiscussions,
  } = useQuery({
    queryKey: ["forum", "discussions", categoryId],
    queryFn: async () => {
      const data = await getForumDiscussions({
        token: auth.user.token,
        categoryId: categoryId,
      });
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(auth.user?.token && categoryId),
    staleTime: 30_000,
  });

  const error = mutationError || (discussionsError ? discussionsError.message : null);

  const hasStaffRole = Boolean(
    auth.user?.roles?.includes("admin") || 
    auth.user?.roles?.includes("editingteacher") || 
    auth.user?.roles?.includes("teacher")
  );
  const canPin = Boolean(hasStaffRole || !auth.user?.roles);

  const categoryColor = getCategoryColor(categoryName || "general");

  const hasMessage = Boolean(newContent.trim() || files.length);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !hasMessage || isCreating) return;
    try {
      setIsCreating(true);
      setMutationError(null);
      await createForumDiscussion({
        token: auth.user.token,
        categoryId: categoryId,
        title: newTitle,
        content: newContent,
        files,
        onProgress: setUploadProgress,
        isPinned: isPinned,
        authorId: auth.user.id,
      });
      setNewTitle("");
      setNewContent("");
      setFiles([]);
      setUploadProgress(0);
      setIsPinned(false);
      setShowForm(false);
      await fetchDiscussions();
      queryClient.invalidateQueries({ queryKey: ["forum"] });
    } catch (err) {
      setMutationError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTogglePin = async (discussionId) => {
    try {
      setMutationError(null);
      await togglePinDiscussion({
        token: auth.user.token,
        discussionId,
      });
      await fetchDiscussions();
      queryClient.invalidateQueries({ queryKey: ["forum"] });
    } catch (err) {
      setMutationError(err.message);
    }
  };

  const handleDeleteDiscussion = (discussion) => {
    if (typeof discussion === "object" && discussion !== null) {
      setDiscussionToDelete(discussion);
    } else {
      const found = discussions.find((d) => Number(d.id) === Number(discussion));
      setDiscussionToDelete(found || { id: discussion, title: "esta discusión" });
    }
  };

  const handleConfirmDelete = async () => {
    if (!discussionToDelete?.id) return;
    const targetId = discussionToDelete.id;
    try {
      setIsDeleting(true);
      setMutationError(null);
      await deleteForumDiscussion({
        token: auth.user.token,
        discussionId: targetId,
      });
      // Optimistically remove from latestPosts cache so "Últimas publicaciones" updates immediately
      queryClient.setQueryData(["forum", "latestPosts"], (old) => {
        if (!Array.isArray(old)) return old;
        return old.filter((p) => Number(p.discussion_id) !== Number(targetId));
      });
      setDiscussionToDelete(null);
      await fetchDiscussions();
      queryClient.invalidateQueries({ queryKey: ["forum"] });
    } catch (err) {
      setMutationError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto max-w-[1620px] w-full" style={{ padding: "24px 15px", minHeight: "calc(100vh - 106px)" }}>
        {loading ? (
          <>
            <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
              <Skeleton
                width={180}
                height={28}
                borderRadius={6}
                baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                highlightColor={isDark ? "#252642" : "#f1f5f9"}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
              <Skeleton
                width={260}
                height={40}
                borderRadius={8}
                baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                highlightColor={isDark ? "#252642" : "#f1f5f9"}
              />
              <Skeleton
                width={160}
                height={48}
                borderRadius={12}
                baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                highlightColor={isDark ? "#252642" : "#f1f5f9"}
              />
            </div>
            <DiscussionCardSkeleton count={4} />
          </>
        ) : (
          <>
            {/* Top Navigation & Breadcrumbs */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <button
                type="button"
                onClick={() => navigate("/foro")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "none",
                  border: "none",
                  color: "#ECB236",
                  cursor: "pointer",
                  fontSize: "var(--font-size-body, 16px)",
                  fontWeight: 600,
                  padding: "6px 0",
                  fontFamily: "'Montserrat', sans-serif",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <ArrowLeft size={20} />
                Volver a todas las categorías
              </button>

              <div
                role="navigation"
                aria-label="Breadcrumb"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "var(--font-size-min, 15px)",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                <span
                  onClick={() => navigate("/foro")}
                  style={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    cursor: "pointer",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ECB236")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? "#94a3b8" : "#64748b")}
                >
                  Foro
                </span>
                <ChevronRight size={16} color={isDark ? "#64748b" : "#94a3b8"} />
                <span
                  style={{
                    color: "#ECB236",
                    fontWeight: 600,
                    maxWidth: "280px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {categoryName || "Categoría"}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", gap: "16px", flexWrap: "wrap" }}>
              <div>
                <h1 className="font-outfit" style={{ color: theme.colors.textColor || theme.colors.textColorNavbar, margin: 0, fontSize: "2.5rem", fontWeight: 700 }}>
                  {categoryName || "Discusiones"}
                </h1>
                <p style={{ margin: "4px 0 0", color: isDark ? "#94a3b8" : "#64748b", fontSize: "var(--font-size-body, 16px)" }}>
                  Discusiones y temas de debate
                </p>
              </div>
              <button
                onClick={() => setShowForm(!showForm)}
                style={{
                  padding: "16px 28px",
                  borderRadius: "12px",
                  border: "none",
                  backgroundColor: "#ECB236",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontSize: "1.7rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 12px rgba(236, 178, 54, 0.3)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(236, 178, 54, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(236, 178, 54, 0.3)";
                }}
              >
                {showForm ? <X size={20} /> : <Plus size={20} />}
                {showForm ? "Cancelar" : "Nueva discusión"}
              </button>
            </div>

            {showForm && (
              <div style={{ animation: "slideDown 0.3s ease-out" }}>
                <style>{`
                  @keyframes slideDown {
                    from {
                      opacity: 0;
                      transform: translateY(-20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}</style>
                <form
                  onSubmit={handleCreate}
                  style={{
                    marginBottom: "24px",
                    padding: "28px",
                    backgroundColor: isDark ? "#141528" : (theme.colors.cardBackground || "#ffffff"),
                    borderRadius: "12px",
                    border: `1px solid ${isDark ? "#334155" : (theme.colors.border || "#e2e8f0")}`,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  }}
                >
                {/* Form Header */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      backgroundColor: `${categoryColor}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: categoryColor,
                    }}
                  >
                    <MessageSquare size={20} />
                  </div>
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        color: theme.colors.textColor || theme.colors.textColorNavbar,
                        fontSize: "1.8rem !important",
                        fontWeight: 600,
                      }}
                    >
                      Nueva discusión
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: isDark ? "#64748b" : "#94a3b8",
                        fontSize: "var(--font-size-min, 15px)",
                      }}
                    >
                      En {categoryName || "General"}
                    </p>
                  </div>
                </div>

                <input
                  ref={titleRef}
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Título de la discusión..."
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: `2px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                    backgroundColor: isDark ? "#0f1221" : (theme.colors.inputBackground || "#f8fafc"),
                    color: theme.colors.textColor || theme.colors.textColorNavbar,
                    fontSize: "var(--font-size-body, 16px)",
                    marginBottom: "16px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => {
                    lastFocusedFieldRef.current = "title";
                    e.target.style.borderColor = categoryColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDark ? "#334155" : "#e2e8f0";
                  }}
                />
                <textarea
                  ref={contentRef}
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  placeholder="Contenido del primer mensaje..."
                  rows={5}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: `2px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                    backgroundColor: isDark ? "#0f1221" : (theme.colors.inputBackground || "#f8fafc"),
                    color: theme.colors.textColor || theme.colors.textColorNavbar,
                    fontSize: "var(--font-size-body, 16px)",
                    marginBottom: "20px",
                    resize: "vertical",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 500,
                    lineHeight: 1.6,
                    outline: "none",
                    transition: "border-color 0.2s",
                    minHeight: "120px",
                  }}
                  onFocus={(e) => {
                    lastFocusedFieldRef.current = "content";
                    e.target.style.borderColor = categoryColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDark ? "#334155" : "#e2e8f0";
                  }}
                />
                {policyError && <p role="alert">{policyError.message}</p>}
                {canPin && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "16px" }}>
                    <input
                      type="checkbox"
                      id="pinDiscussion"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        accentColor: categoryColor,
                      }}
                    />
                    <label
                      htmlFor="pinDiscussion"
                      style={{
                        color: theme.colors.textColor || theme.colors.textColorNavbar,
                        fontSize: "var(--font-size-min, 15px)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span style={{ color: categoryColor }}>
                        Fijar esta discusión
                      </span>
                    </label>
                  </div>
                )}

                <div className="forum-reply-actions">
                  <div className="forum-reply-tools">
                  <EmojiPickerButton onEmojiSelect={handleEmojiSelect} disabled={isCreating} />
                  <AttachmentPicker files={files} onChange={setFiles} policy={attachmentPolicy} disabled={isCreating} progress={uploadProgress} showLimits={false} />
                  </div>
                  <button
                    type="submit"
                    disabled={!newTitle.trim() || !hasMessage || isCreating}
                    style={{
                      padding: "14px 32px",
                      borderRadius: "10px",
                      border: "none",
                      backgroundColor: newTitle.trim() && hasMessage && !isCreating ? "#ECB236" : "#ccc",
                      color: newTitle.trim() && hasMessage && !isCreating ? "#fff" : "#666",
                      fontWeight: 700,
                      fontSize: "var(--font-size-body, 16px)",
                      cursor: newTitle.trim() && hasMessage && !isCreating ? "pointer" : "not-allowed",
                      boxShadow: newTitle.trim() && hasMessage && !isCreating ? "0 4px 12px rgba(236, 178, 54, 0.3)" : "none",
                      transition: "all 0.2s ease",
                      opacity: isCreating ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (newTitle.trim() && hasMessage && !isCreating) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(236, 178, 54, 0.4)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = newTitle.trim() && hasMessage && !isCreating ? "0 4px 12px rgba(236, 178, 54, 0.3)" : "none";
                    }}
                  >
                    {isCreating ? "Creando..." : "Crear discusión"}
                  </button>
                </div>
              </form>
            </div>
            )}

            {error && (
              <div style={{ padding: "16px", textAlign: "center", color: "red", marginBottom: "16px" }}>
                {error}
              </div>
            )}

            <DiscussionList
              discussions={discussions}
              categoryName={categoryName}
              onTogglePin={handleTogglePin}
              onDeleteDiscussion={handleDeleteDiscussion}
              canPin={canPin}
              onNewDiscussion={() => setShowForm(true)}
              isCreating={showForm}
            />

            <DeleteConfirmationModal
              isOpen={Boolean(discussionToDelete)}
              onClose={() => !isDeleting && setDiscussionToDelete(null)}
              onConfirm={handleConfirmDelete}
              title="¿Eliminar discusión?"
              description={
                discussionToDelete?.title
                  ? `¿Estás seguro de que deseas eliminar "${discussionToDelete.title}"? Esta acción no se puede deshacer y borrará todas sus respuestas.`
                  : "¿Estás seguro de que deseas eliminar esta discusión? Esta acción no se puede deshacer y borrará todas sus respuestas."
              }
              isDeleting={isDeleting}
            />
          </>
        )}
      </div>
  );
};

export default CategoryView;
